import { useEffect, useMemo } from "react";

import { Link, Navigate, useParams } from "react-router-dom";

import { ArrowPathIcon, PencilIcon } from "@heroicons/react/24/outline";

import { Alert } from "../../components/base/Alert";
import { UserStatus } from "../../models/User/User";
import Card from "../../components/shared/Card/Card";
import { Button } from "../../components/base/Button";
import MiniTable from "../../components/base/MiniTable";
import useCustomer from "../../data/Customer/useCustomer";
import { errorResolver } from "../../utils/errorResolver";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useCategoryMutations from "../../data/Category/useCategoryMutations";
import useTemplateMutations from "../../data/Template/useTemplateMutations";
import useCustomerUsers from "../../data/CustomerUser/useCustomerUsers";
import { BasicInformation } from "../../components/ViewCustomer/BasicInformation";
import { CustomerUsersList } from "../../components/ViewCustomer/CustomerUserList";
import { CustomerFeatureList } from "../../components/ViewCustomer/CustomerFeatureList";
import { CustomerSettingsList } from "../../components/ViewCustomer/CustomerSettingsList";
import { CustomerApplicationList } from "../../components/ViewCustomer/CustomerApplicationList";
import { CustomerIntegrationList } from "../../components/ViewCustomer/CustomerIntegrationsList";
import { CustomerShopifyStoreList } from "../../components/ViewCustomer/CustomerShopifyStoreList";
import { CustomerSsoConfigSection } from "../../components/ViewCustomer/CustomerSsoConfigSection";
import { CustomerSendingDomainList } from "../../components/ViewCustomer/CustomerSendingDomainList";
import EmbedShopify from "./EmbedShopify";
import { ReadinessSummaryCard } from "../../components/ViewCustomer/ReadinessSummaryCard";

export function ViewCustomer() {
  const { customerId } = useParams();
  const { customer } = useCustomer(customerId!);
  const { customerUsers } = useCustomerUsers(customerId!);
  const { setInfo } = usePageNameContext();
  const { syncCategories } = useCategoryMutations();
  const { syncTemplates } = useTemplateMutations();

  useEffect(() => {
    if (!customer.isLoading && customer.data) {
      setInfo({
        name: customer.data.name,
        desc: `View ${customer.data.name}'s details`,
        pageTitle: `View Customer | ${customer.data.name}`,
      });
    } else {
      setInfo({
        name: "View Customer",
        desc: "View customer details",
        pageTitle: "View Customer",
      });
    }
  }, [customer.isLoading, customer.data]);

  const activeUserCount = useMemo(() => {
    if (!customerUsers.isLoading && customerUsers.data) {
      return customerUsers.data.filter(
        (user) => user.status === UserStatus.Enabled
      ).length;
    }
  }, [customerUsers.isLoading, customerUsers.data]);

  if (!customerId) {
    return <Navigate to="/customers" />;
  } else if (customer.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <LoadingWheel />
      </div>
    );
  } else if (customer.isError || !customer.data) {
    return (
      <Alert
        severity="error"
        title="Error getting customer"
        message={errorResolver(customer.error)}
      />
    );
  } else {
    return (
      <>
        <div className="mb-6 flex justify-between">
          <div className="flex items-center space-x-3">
            <Link to={`/customer/edit/${customerId}`}>
              <Button className="flex items-center space-x-2">
                <PencilIcon className="h-5 w-5 -ml-1" />
                <p>Edit Customer</p>
              </Button>
            </Link>
            <Button
              variant="outlined"
              className="flex items-center space-x-2"
              onClick={() => syncCategories.mutate([customerId!])}
              disabled={syncCategories.isPending}
            >
              <ArrowPathIcon className={`h-5 w-5 -ml-1 ${syncCategories.isPending ? "animate-spin" : ""}`} />
              <p>Sync Categories</p>
            </Button>
            <Button
              variant="outlined"
              className="flex items-center space-x-2"
              onClick={() => syncTemplates.mutate([customerId!])}
              disabled={syncTemplates.isPending}
            >
              <ArrowPathIcon className={`h-5 w-5 -ml-1 ${syncTemplates.isPending ? "animate-spin" : ""}`} />
              <p>Sync Templates</p>
            </Button>
          </div>

          <EmbedShopify customerId={customerId} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="grid grid-cols-1 gap-4">
            <BasicInformation customer={customer.data} />
            <CustomerFeatureList customerId={customer.data.id} />
            <CustomerSsoConfigSection customerId={customer.data.id} />
            <CustomerSendingDomainList customerId={customer.data.id} />
            <Card title="Active Users">
              <div className="flex items-center justify-center py-5">
                <p className="text-8xl m-0 -mt-4">{activeUserCount}</p>
              </div>
            </Card>
            <ReadinessSummaryCard customerId={customer.data.id} />
            <Card title="Infrastructure Status" bodyClassName="p-4">
              <MiniTable>
                <MiniTable.Header>
                  <MiniTable.HeaderCell>Application</MiniTable.HeaderCell>
                  <MiniTable.HeaderCell className="text-center">
                    Version
                  </MiniTable.HeaderCell>
                </MiniTable.Header>
                <MiniTable.Body>
                  <MiniTable.Row>
                    <MiniTable.Cell>Dashboard</MiniTable.Cell>
                    <MiniTable.Cell className="text-center">
                      1.2.4
                    </MiniTable.Cell>
                  </MiniTable.Row>
                  <MiniTable.Row>
                    <MiniTable.Cell>API</MiniTable.Cell>
                    <MiniTable.Cell className="text-center">
                      1.1.3
                    </MiniTable.Cell>
                  </MiniTable.Row>
                </MiniTable.Body>
              </MiniTable>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 md:col-span-2 gap-4 content-start">
            <div className="md:col-span-2 space-y-4">
              <CustomerApplicationList customerId={customer.data.id} />
              <CustomerSettingsList customerId={customer.data.id} />
            </div>
          </div>

          <div className="md:col-span-3 space-y-4">
            <CustomerUsersList customerId={customer.data.id} />
            <CustomerIntegrationList customerId={customer.data.id} />
            <CustomerShopifyStoreList customerId={customer.data.id} />
          </div>
        </div>
      </>
    );
  }
}
